import {ErrorMessage, Field, Form, Formik} from "formik";
import postData from "../services/postData";
import Modal from "./Modal";
import {useState} from "react";
import {ScrollToFieldError} from "./formik/ScrollToFieldError";
import {formSchema} from "../schema/formSchema";
import SkodaSecondDataInfo from "./SkodaSecondDataInfo";
import SkodaDataInfo from "./SkodaDataInfo";
import SkodaConsentDataInfo from "./SkodaConsentDataInfo";
import SkodaConsentSecondDataInfo from "./SkodaConsentSecondDataInfo";
import { carMarks } from "./SkodaCarMarks";

// Funkcja do kodowania nazwy modelu w URL-safe format
const encodeCarModel = (modelName) => {
    return encodeURIComponent(modelName.replace(/\s+/g, '-').toLowerCase());
};

// Funkcja do dekodowania nazwy modelu z URL
const decodeCarModel = (encodedModel) => {
    return decodeURIComponent(encodedModel).replace(/-/g, ' ');
};

const EPIQ_CAR = carMarks.find((car) => car.slug === 'epiq') || carMarks[0];

export default function ServiceForm({setLoadingSpinnerOpen}) {

    const formType = 'epiq,oferta,202609';

    const [successModal, setSuccessModal] = useState(false);
    const [failureModal, setFailureModal] = useState(false);
    const [formIsSubmitting, setFormIsSubmitting] = useState(false);

    const generateModelLink = (carName) => {
        const encodedModel = encodeCarModel(carName);
        const url = new URL(window.location);
        url.searchParams.set('model', encodedModel);
        return url.toString();
    };

    // Eksportuj funkcję do użytku zewnętrznego (np. do remarketingu)
    window.generateModelLink = generateModelLink;
    
    // Funkcja do sprawdzania aktualnego modelu w URL (przydatne do remarketingu)
    const getCurrentModelFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryModel = urlParams.get('model');
        const hash = window.location.hash;
        const hashModelMatch = hash.match(/model=([^&]+)/);
        
        if (queryModel) {
            return decodeCarModel(queryModel);
        } else if (hashModelMatch) {
            return decodeCarModel(hashModelMatch[1]);
        }
        return null;
    };
    
    // Eksportuj funkcję do sprawdzania aktualnego modelu
    window.getCurrentModelFromURL = getCurrentModelFromURL;

    const dataLayerPush = (orderId, leadId) => {
        try {
            window.dataLayer.push({
                'event': 'formularze',
                'wypelniony-formularz': 'oferta-zamowiono',
                'orderid': orderId,
                'leadid': leadId
            });
            console.log('dataLayer.push ok:', orderId);
        } catch (err) {
            console.error('dataLayer.push error:', err);
        }
    };

    const syneriseSendFormData = (orderId, values) => {
        if (!('SR' in window)) {
            console.error('SR undefined');
            return;
        }

        const snrData = {
            orderID: orderId,
            firstname: values.firstName, //imie
            email: values.email, //email
            ...(values.newsletterAgreement ? {newsletterAgreement: 'enabled'} : {}), // brak zgody => atrybut w ogóle nie wysyłany (zgodnie z dokumentacją wdrożeniową)
            newsletter_info: values.newsletter_info,
            statute: values.statute,
            selectedCar: EPIQ_CAR.name,
        };
        try {
            window.SR.event.sendFormData(`${EPIQ_CAR.slug},oferta,bp`, snrData)
                .then((res) => {
                    console.log(formType);
                    console.log(res);
                })
                .catch((err) => {
                    console.log(formType);
                    console.error(err);
                });
        } catch (err) {
            console.error('SR error', err);
        }
    };

    const insertSyneriseScript = () => {
        const syneriseScript = document.createElement('script');
        syneriseScript.innerHTML = `
            function onSyneriseLoad() {
                SR.init({
                    'trackerKey':'F79A2C6D-7D1D-2E3A-F1D4-997A40970066',
                });
            }

            (function(s,y,n,e,r,i,se){s['SyneriseObjectNamespace']=r;s[r]=s[r]||[],s[r]._t=1*new Date(),
                s[r]._i=0,s[r]._l=i;var z=y.createElement(n),se=y.getElementsByTagName(n)[0];z.async=1;z.src=e;
                se.parentNode.insertBefore(z,se);z.onload=z.onreadystatechange=function(){var rdy=z.readyState;
                    if(!rdy||/complete|loaded/.test(z.readyState)){s[i]();z.onload = null;z.onreadystatechange=null;}};
            })(window,document,'script','//www.snrcdn.net/sdk/3.0/synerise-javascript-sdk.min.js','SR', 'onSyneriseLoad');
        `;
        document.body.appendChild(syneriseScript);
    }

    return (
        <Formik
            initialValues={{
                firstName: '',
                phone: '',
                email: '',
                selectedCar: EPIQ_CAR.name,
                newsletterViaEmail: false,
                newsletterViaMessage: false,
                newsletterViaPhone: false,
                newsletterReceiving: false,
                newsletterAgreement: false,
                newsletter_info: false,
                statute: false,
            }}
            validationSchema={formSchema}
            onSubmit={(values, actions) => {
                actions.setSubmitting(false);
                setFormIsSubmitting(true);

                if(window.SR === undefined) {
                    insertSyneriseScript();
                }
                // Add selected car data to form values
                const formDataWithCar = {
                    ...values,
                    selectedCar: EPIQ_CAR.name,
                };

                const intervalId = setInterval(() => {
                    if ((window.SR !== undefined)) {
                        clearInterval(intervalId);

                        postData(formDataWithCar)
                            .then((data) => {
                                if (data && data.status === 'ok') {
        
                                    dataLayerPush(data.orderId, data.leadId);
                                    syneriseSendFormData(data.orderId, values);
                                    actions.resetForm();
                                    setSuccessModal(true);
                                } else {
                                    setFailureModal(true);
                                }
                            })
                            .catch((error) => {
                                setFailureModal(true);
                            })
                            .finally(() => {
                                setFormIsSubmitting(false);
                                // setLoadingSpinnerOpen(false);
                                window.scrollTo({
                                    top: 0,
                                    left: 0,
                                    behavior: "smooth"
                                });
                            });
                    }
                }, 300);

                // Set failure when no SR in window after 5s
                setTimeout(() => {
                    clearInterval(intervalId);
                    if (window.SR === undefined) {
                        setFailureModal(true);                    
                        setFormIsSubmitting(false);
                        window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: "smooth"
                        });
                        return;
                    }
                }, 5000);
            }}
        >
            {() => {
                return (
                <>
                    <Modal open={successModal} setOpen={setSuccessModal}>
                        <b>Dziękujemy za wybór Škody</b>
                    </Modal>

                    <Modal open={failureModal} setOpen={setFailureModal}>
                        Wystąpił błąd!<br/>
                        Prosimy spróbować później.
                    </Modal>

                    <Form className="form-container form-container js-subscribe-form" method={`post`}>
                        <ScrollToFieldError/>
                            <div className="form-row form-model">
                                <label className="form-label">MODEL:</label>
                                <div className="form-row form-row--buttons">
                                    <div style={{width: '100%', marginBottom: '10px'}}>
                                        <h4 style={{display: "inline-block", margin: 0}}>{EPIQ_CAR.name}</h4>
                                        <p style={{margin: '10px 0 0 0'}} dangerouslySetInnerHTML={{ __html: EPIQ_CAR.desc }}></p>
                                        <img src={EPIQ_CAR.image} alt={EPIQ_CAR.name} style={{maxWidth: '100%', height: 'auto', marginTop: '10px'}} />
                                    </div>
                                </div>
                                <Field name="selectedCar" type="hidden" />
                            </div>

                        <div className="form-fields">

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="firstName">Imię</label>
                                <Field id="firstName" name="firstName" type="text" className="form-input" maxLength={50}></Field>
                                <ErrorMessage name="firstName">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="text">Telefon</label>
                                <Field id="phone" name="phone" type="text" className="form-input" maxLength={255}></Field>
                                <ErrorMessage name="phone">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="email">E-mail<br/><span className="form-label--optional">(opcjonalnie)</span></label>
                                <Field id="email" name="email" type="email" className="form-input" maxLength={255}></Field>
                                <ErrorMessage name="email">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                        </div>


                        <div className="form-row form-row--buttons form-submit-wrapper">
                            <button type="submit" className="button button--primary button--send" disabled={formIsSubmitting}>Wyślij zgłoszenie</button>
                        </div>

                        <div className="form-consent">
                            <div className="form-row">
                                <div className="consent-scroll">
                                    <p>
                                        Pozostawienie przez Ciebie danych i wysłanie formularza oznacza Twoją zgodę dla Volkswagen Group Polska sp. z o.o. 
                                        oraz poprzez Twój wybór także dla wybranego Autoryzowanego Dealera marki Škoda [dane Dealera możesz znaleźć na stronie <a style={{color: "#0E3A2F"}} href="https://retailers.skoda-auto.com/456/pl-pl" target="_blank" rel="noreferrer"><b>https://retailers.skoda-auto.com/456/pl-pl</b></a>] 
                                        na skontaktowanie się z Tobą w celu przedstawienia Ci informacji na temat zakupu samochodu marki Skoda 
                                        oraz ogólnych informacji dot. możliwych sposobów finansowania z użyciem telefonu lub adresu e-mail. Skontaktujemy się z Tobą, zweryfikujemy 
                                        Twoje dane i potwierdzimy Twoje zainteresowanie informacjami na temat oferty. Następnie Twoje zapytanie przekażemy wybranemu przez 
                                        Ciebie Autoryzowanemu Dealerowi marki Škoda, który skontaktuje z Tobą celem obsługi Twojego zapytania (Preferowany Dealer). 
                                        Podanie Twoich danych jest dobrowolne, lecz niezbędne do obsługi Twojego zapytania. Administratorem danych jest 
                                        Volkswagen Group Polska sp. z o.o. z siedzibą w Poznaniu (61-037), ul. Krańcowa 44 wpisana do Rejestru Przedsiębiorców prowadzonego przez 
                                        Sąd Rejonowy w Poznaniu - Nowe Miasto i Wilda, VIII Wydział Gospodarczy pod numerem KRS 0000327143, NIP 782-24-63-563, REGON 301062169 (VGP) 
                                        oraz Preferowany Dealer, działający na zasadzie współadministrowania. W każdej chwili możesz cofnąć swoją zgodę i zrezygnować z otrzymania informacji dot. 
                                        oferty informując konsultanta Centrum Informacji lub Preferowanego Dealera o rezygnacji z dalszego kontaktu w tym zakresie (utrata zainteresowania ofertą 
                                        będzie dla nas jednoznaczna z cofnięciem Twojej zgody na obsługę tego zapytania). Swoje dodatkowe zgody możesz cofnąć poprzez formularz umieszczony na 
                                        stronie internetowej <a style={{color: "#0E3A2F"}} href="https://www.twoja.skoda.pl/formularze/formularz-cofniecia-zgod" target="_blank" rel="noreferrer"><b>https://www.twoja.skoda.pl/formularze/formularz-cofniecia-zgod</b></a> albo bezpośrednio w kontakcie z VGP lub z Autoryzowanym Dealerem / Autoryzowanym 
                                        Partnerem Serwisowym marki Škoda.
                                    </p>
                                </div>
                            </div>

                            <div className="form-row">
                                <p className="small-text"><b>Poniższe zgody są opcjonalne i dobrowolne, a ich udzielenie lub odmowa są niezależne od obsługi twojego zapytania.</b></p>
                            </div>

                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="newsletterReceiving" name="newsletterReceiving" type="checkbox"></Field>
                                    <label htmlFor="newsletterReceiving" className="form-check-label">
                                        Zgadzam się na używanie przez Volkswagen Group Polska Sp. z o.o. (VGP) oraz Škoda Auto a.s. przekazanych danych, 
                                        danych zebranych o moich zapytaniach i zainteresowaniach, ofertach, zamówieniach i zleceniach w ramach...
                                        <SkodaConsentDataInfo/>
                                    </label>
                                </div>
                                <ErrorMessage name="newsletterReceiving">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row">
                                <div className="form-checkbox">
                                    <span className="form-span">Chcę otrzymać od VGP w/w informacje handlowe i marketingowe poprzez:</span>

                                    <Field className="form-check-input" id="newsletterViaEmail" name="newsletterViaEmail" type="checkbox"></Field>
                                    <label htmlFor="newsletterViaEmail" className="form-check-label">E-mail</label>

                                    <Field className="form-check-input" id="newsletterViaMessage" name="newsletterViaMessage" type="checkbox"></Field>
                                    <label htmlFor="newsletterViaMessage" className="form-check-label">SMS/MMS</label>

                                    <Field className="form-check-input" id="newsletterViaPhone" name="newsletterViaPhone" type="checkbox"></Field>
                                    <label htmlFor="newsletterViaPhone" className="form-check-label">Telefon</label>
                                </div>
                                <ErrorMessage name="newsletterViaEmail">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <SkodaConsentSecondDataInfo/>
                            
                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="newsletterAgreement" name="newsletterAgreement" type="checkbox"></Field>
                                    <label htmlFor="newsletterAgreement" className="form-check-label">
                                        Chcę otrzymywać Newsletter od Volkswagen Group Polska Sp. z o.o. z wykorzystaniem adresu e-mail oraz pozostałych podanych przeze mnie Danych.                               </label>
                                </div>
                                <ErrorMessage name="newsletterAgreement">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="newsletter_info" name="newsletter_info" type="checkbox"></Field>
                                    <label htmlFor="newsletter_info" className="form-check-label">
                                        W ramach Newslettera zgadzam się na otrzymywanie od Volkswagen Group Polska Sp. z o.o. w/w informacji mogących stanowić informacje handlowe i marketingowe.                           </label>
                                </div>
                                <ErrorMessage name="newsletter_info">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="statute" name="statute" type="checkbox"></Field>
                                    <label htmlFor="statute" className="form-check-label">
                                        Akceptuję <a href="https://twoja.skoda.pl/brandportal/newsletter/rules/0afc6cb8156bd43640a6a5f47d5f655f4c45c2b1.pdf" target="_blank" rel="noreferrer">regulamin</a> newslettera
                                    </label>
                                </div>
                                <ErrorMessage name="statute">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                            <SkodaSecondDataInfo/>
                            <SkodaDataInfo/>
                        </div>
                    </Form>
                </>
            );
        }}
        </Formik>
    );
}
